// src/MosaicWindow.tsx
import classNames from "classnames";
import { defer, dropRight, isEmpty, isEqual, values } from "lodash-es";
import React, { useContext } from "react";
import {
  useDrag,
  useDrop
} from "react-dnd";
import { DEFAULT_CONTROLS_WITHOUT_CREATION, DEFAULT_CONTROLS_WITH_CREATION } from "./buttons/defaultToolbarControls.mjs";
import { Separator } from "./buttons/Separator.mjs";
import { MosaicContext, MosaicWindowContext } from "./contextTypes.mjs";
import { MosaicDropTargetPosition } from "./internalTypes.mjs";
import { MosaicDropTarget } from "./MosaicDropTarget.mjs";
import { MosaicDragType } from "./types.mjs";
import { createDragToUpdates } from "./util/mosaicUpdates.mjs";
import { getAndAssertNodeAtPathExists } from "./util/mosaicUtilities.mjs";
import { OptionalBlueprint } from "./util/OptionalBlueprint.mjs";
var InternalMosaicWindow = class extends React.Component {
  static defaultProps = {
    additionalControlButtonText: "More",
    draggable: true,
    renderPreview: ({ title }) => /* @__PURE__ */ React.createElement("div", { className: "mosaic-preview" }, /* @__PURE__ */ React.createElement("div", { className: "mosaic-window-toolbar" }, /* @__PURE__ */ React.createElement("div", { className: "mosaic-window-title" }, title)), /* @__PURE__ */ React.createElement("div", { className: "mosaic-window-body" }, /* @__PURE__ */ React.createElement("h4", null, title), /* @__PURE__ */ React.createElement(OptionalBlueprint.Icon, { className: "default-preview-icon", size: "large", icon: "APPLICATION" }))),
    renderToolbar: null
  };
  static contextType = MosaicContext;
  state = {
    additionalControlsOpen: false
  };
  rootElement = null;
  render() {
    const {
      className,
      isOver,
      renderPreview,
      additionalControls,
      connectDropTarget,
      connectDragPreview,
      draggedMosaicId,
      disableAdditionalControlsOverlay
    } = this.props;
    return /* @__PURE__ */ React.createElement(MosaicWindowContext.Provider, { value: this.childContext }, connectDropTarget(
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames("mosaic-window mosaic-drop-target", className, {
            "drop-target-hover": isOver && draggedMosaicId === this.context.mosaicId,
            "additional-controls-open": this.state.additionalControlsOpen
          }),
          ref: (element) => {
            this.rootElement = element;
          }
        },
        this.renderToolbar(),
        /* @__PURE__ */ React.createElement("div", { className: "mosaic-window-body" }, this.props.children),
        !disableAdditionalControlsOverlay && /* @__PURE__ */ React.createElement(
          "div",
          {
            className: "mosaic-window-body-overlay",
            onClick: () => {
              this.setAdditionalControlsOpen(false);
            }
          }
        ),
        /* @__PURE__ */ React.createElement("div", { className: "mosaic-window-additional-actions-bar" }, additionalControls),
        connectDragPreview(renderPreview(this.props)),
        /* @__PURE__ */ React.createElement("div", { className: "drop-target-container" }, values(MosaicDropTargetPosition).map(this.renderDropTarget))
      )
    ));
  }
  getToolbarControls() {
    const { toolbarControls, createNode } = this.props;
    if (toolbarControls) {
      return toolbarControls;
    } else if (createNode) {
      return DEFAULT_CONTROLS_WITH_CREATION;
    } else {
      return DEFAULT_CONTROLS_WITHOUT_CREATION;
    }
  }
  renderToolbar() {
    const { title, draggable, additionalControls, additionalControlButtonText, path, renderToolbar } = this.props;
    const { additionalControlsOpen } = this.state;
    const toolbarControls = this.getToolbarControls();
    const draggableAndNotRoot = draggable && path.length > 0;
    const connectIfDraggable = draggableAndNotRoot ? this.props.connectDragSource : (el) => el;
    if (renderToolbar) {
      const connectedToolbar = connectIfDraggable(renderToolbar(this.props, draggable));
      return /* @__PURE__ */ React.createElement("div", { className: classNames("mosaic-window-toolbar", { draggable: draggableAndNotRoot }) }, connectedToolbar);
    }
    const titleDiv = connectIfDraggable(
      /* @__PURE__ */ React.createElement("div", { title, className: "mosaic-window-title" }, title)
    );
    const hasAdditionalControls = !isEmpty(additionalControls);
    return /* @__PURE__ */ React.createElement("div", { className: classNames("mosaic-window-toolbar", { draggable: draggableAndNotRoot }) }, titleDiv, /* @__PURE__ */ React.createElement("div", { className: classNames("mosaic-window-controls", OptionalBlueprint.getClasses("BUTTON_GROUP")) }, hasAdditionalControls && /* @__PURE__ */ React.createElement(
      "button",
      {
        onClick: () => this.setAdditionalControlsOpen(!additionalControlsOpen),
        className: classNames(
          OptionalBlueprint.getClasses(this.context.blueprintNamespace, "BUTTON", "MINIMAL"),
          OptionalBlueprint.getIconClass(this.context.blueprintNamespace, "MORE"),
          {
            [OptionalBlueprint.getClasses(this.context.blueprintNamespace, "ACTIVE")]: additionalControlsOpen
          }
        )
      },
      /* @__PURE__ */ React.createElement("span", { className: "control-text" }, additionalControlButtonText)
    ), hasAdditionalControls && /* @__PURE__ */ React.createElement(Separator, null), toolbarControls));
  }
  renderDropTarget = (position) => {
    const { path } = this.props;
    return /* @__PURE__ */ React.createElement(MosaicDropTarget, { position, path, key: position });
  };
  checkCreateNode() {
    if (this.props.createNode == null) {
      throw new Error("Operation invalid unless `createNode` is defined");
    }
  }
  split = (...args) => {
    this.checkCreateNode();
    const { createNode, path } = this.props;
    const { mosaicActions } = this.context;
    const root = mosaicActions.getRoot();
    const direction = this.rootElement.offsetWidth > this.rootElement.offsetHeight ? "row" : "column";
    return Promise.resolve(createNode(...args)).then(
      (second) => mosaicActions.replaceWith(path, {
        direction,
        second,
        first: getAndAssertNodeAtPathExists(root, path)
      })
    );
  };
  swap = (...args) => {
    this.checkCreateNode();
    const { mosaicActions } = this.context;
    const { createNode, path } = this.props;
    return Promise.resolve(createNode(...args)).then((node) => mosaicActions.replaceWith(path, node));
  };
  setAdditionalControlsOpen = (additionalControlsOpenOption) => {
    const additionalControlsOpen = additionalControlsOpenOption === "toggle" ? !this.state.additionalControlsOpen : additionalControlsOpenOption;
    this.setState({ additionalControlsOpen });
    this.props.onAdditionalControlsToggle?.(additionalControlsOpen);
  };
  getPath = () => this.props.path;
  connectDragSource = (connectedElements) => {
    const { connectDragSource } = this.props;
    return connectDragSource(connectedElements);
  };
  childContext = {
    // @ts-ignore
    blueprintNamespace: this.context.blueprintNamespace,
    mosaicWindowActions: {
      split: this.split,
      replaceWithNew: this.swap,
      setAdditionalControlsOpen: this.setAdditionalControlsOpen,
      getPath: this.getPath,
      connectDragSource: this.connectDragSource
    }
  };
};
function ConnectedInternalMosaicWindow(props) {
  const { mosaicActions, mosaicId } = useContext(MosaicContext);
  const [, connectDragSource, connectDragPreview] = useDrag({
    type: MosaicDragType.WINDOW,
    item: (_monitor) => {
      if (props.onDragStart) {
        props.onDragStart();
      }
      const hideTimer = defer(() => mosaicActions.hide(props.path));
      return {
        mosaicId,
        hideTimer
      };
    },
    end: (item, monitor) => {
      const { hideTimer } = item;
      window.clearTimeout(hideTimer);
      const ownPath = props.path;
      const dropResult = monitor.getDropResult() || {};
      const { position, path: destinationPath } = dropResult;
      if (position != null && destinationPath != null && !isEqual(destinationPath, ownPath)) {
        mosaicActions.updateTree(createDragToUpdates(mosaicActions.getRoot(), ownPath, destinationPath, position));
        if (props.onDragEnd) {
          props.onDragEnd("drop");
        }
      } else {
        mosaicActions.updateTree([
          {
            path: dropRight(ownPath),
            spec: {
              splitPercentage: {
                $set: void 0
              }
            }
          }
        ]);
        if (props.onDragEnd) {
          props.onDragEnd("reset");
        }
      }
    }
  });
  const [{ isOver, draggedMosaicId }, connectDropTarget] = useDrop({
    accept: MosaicDragType.WINDOW,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      draggedMosaicId: monitor.getItem()?.mosaicId
    })
  });
  return /* @__PURE__ */ React.createElement(
    InternalMosaicWindow,
    {
      ...props,
      connectDragPreview,
      connectDragSource,
      connectDropTarget,
      isOver,
      draggedMosaicId
    }
  );
}
var MosaicWindow = class extends React.PureComponent {
  render() {
    return /* @__PURE__ */ React.createElement(ConnectedInternalMosaicWindow, { ...this.props });
  }
};
export {
  InternalMosaicWindow,
  MosaicWindow
};
